import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';
import { toast } from 'react-toastify';

const BASE_URL = '/api/v1/survey';

// All API communication for the survey tool (Umfragen): CRUD plus response statistics.
export const useSurvey = () => {
  const { getAuthHeaders } = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  // All surveys with their responseCount (and pageUrl for page-type surveys), newest first.
  const fetchSurveys = useCallback(async () => {
    try {
      const responseData = await sendRequest({
        url: BASE_URL,
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return responseData;
    } catch (err) {
      throw err;
    }
  }, [getAuthHeaders, sendRequest]);

  const getSurveyById = useCallback(
    async surveyId => {
      try {
        const responseData = await sendRequest({
          url: `${BASE_URL}/${surveyId}`,
          method: 'GET',
          headers: getAuthHeaders(),
        });
        return responseData;
      } catch (err) {
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const createSurvey = useCallback(
    async surveyData => {
      try {
        const responseData = await sendRequest({
          url: BASE_URL,
          method: 'POST',
          headers: getAuthHeaders(),
          body: surveyData,
        });
        // A null response means the request was stored for approval — no success toast then.
        if (responseData) {
          toast.success('Umfrage erfolgreich erstellt');
        }
        return responseData;
      } catch (err) {
        if (err.code === 'SURVEY_SLUG_TAKEN') {
          toast.error('Dieser Slug ist bereits vergeben. Bitte einen anderen wählen.');
        } else {
          toast.error('Fehler beim Erstellen der Umfrage');
        }
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const updateSurvey = useCallback(
    async (surveyId, surveyData) => {
      try {
        const responseData = await sendRequest({
          url: `${BASE_URL}/${surveyId}`,
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: surveyData,
        });
        if (responseData) {
          toast.success('Umfrage erfolgreich aktualisiert');
        }
        return responseData;
      } catch (err) {
        if (err.code === 'SURVEY_SLUG_TAKEN') {
          toast.error('Dieser Slug ist bereits vergeben. Bitte einen anderen wählen.');
        } else {
          toast.error('Fehler beim Aktualisieren der Umfrage');
        }
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  // Deletes the survey together with all of its stored responses.
  const deleteSurvey = useCallback(
    async surveyId => {
      try {
        const responseData = await sendRequest({
          url: `${BASE_URL}/${surveyId}`,
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (responseData) {
          toast.success('Umfrage erfolgreich gelöscht');
        }
        return responseData;
      } catch (err) {
        toast.error('Fehler beim Löschen der Umfrage');
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  // Aggregated response statistics for a survey and an optional date range (YYYY-MM-DD).
  const getSurveyStatistics = useCallback(
    async ({ surveyId, from, to } = {}) => {
      try {
        const params = {};
        if (from) params.from = from;
        if (to) params.to = to;
        const responseData = await sendRequest({
          url: `${BASE_URL}/${surveyId}/statistics`,
          method: 'GET',
          headers: getAuthHeaders(),
          params,
        });
        return responseData;
      } catch (err) {
        throw err;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  return {
    isLoading,
    error,
    fetchSurveys,
    getSurveyById,
    createSurvey,
    updateSurvey,
    deleteSurvey,
    getSurveyStatistics,
  };
};
